import { useState, useEffect, useRef, useCallback } from "react";
import * as pako from "pako";
import {
  Editor,
  type TLShapeId,
  type TLBindingCreate,
  type TLStore,
  toRichText,
} from "tldraw";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import PCMPlayer from "@/features/tutor/components/PCMPlayer";
import { useAuthStore } from "@/store/useAuthStore";
import {
  CanvasChunkType,
  makeCanvasChunk,
  isCanvasChunk,
} from "@/canvas/agent/chunk_protocol";
import { convertPartialFocusedShapeToTldrawShape } from "@/shared/format/convertFocusedShapeToTldrawShape";
import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext";
import type { SpaceCanvasContext } from "./useStudentSpaceCanvases";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
  timestamp: number;
};

export type AgentPhase = "idle" | "listening" | "loading" | "responding";

const LOADING_TIMEOUT_MS = 30_000; // 30s hard ceiling before surfacing a silent failure

const storageKey = (spaceId: string) => `spaces-chat-${spaceId}`;

function randomId(): string {
  return crypto.randomUUID();
}

// ---------------------------------------------------------------------------
// Compression helpers (mirroring WebSocketTransport)
// ---------------------------------------------------------------------------

function sendCompressed(ws: WebSocket, obj: unknown): void {
  const json = JSON.stringify(obj);
  const compressed = pako.deflate(new TextEncoder().encode(json));
  ws.send(compressed);
}

function inflateFrame(data: ArrayBuffer): unknown | null {
  try {
    const inflated = pako.inflate(new Uint8Array(data));
    const text = new TextDecoder().decode(inflated);
    return JSON.parse(text);
  } catch (err) {
    console.warn("[useSpaceAgent] malformed compressed frame:", err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Canvas action executor
// Applies a complete canvas action directly to the TLDraw editor.
// Uses the same focused-shape format as the notepad agent so the LLM
// produces compatible output.
// ---------------------------------------------------------------------------

function applySpaceCanvasAction(
  editor: Editor,
  action: Record<string, unknown>,
  personalStore: TLStore | null,
  userId: string | null,
): void {
  try {
    // AI shape mutations run inside mergeRemoteChanges so they're tagged
    // source=remote. Two things we rely on:
    //   • the read-only guard only stops user-source mutations — remote
    //     writes pass through, so the AI keeps drawing even after the teacher
    //     puts the student into read mode;
    //   • the personal-routing patch in spaces-diff-filter early-returns on
    //     `store.isMergingRemoteChanges === true` and writes records straight
    //     to editor.store without mirroring them into personalStore. That's
    //     exactly why we mirror manually below — otherwise the AI shapes
    //     would never reach the DO personal room.
    //
    // We pre-stamp meta.layer = userId so any later edit that DOES run
    // through the routing patch (e.g. the student moves the AI shape) is
    // classified as personal, matching where it actually lives.
    const aiMeta: Record<string, unknown> = {
      role: "spaces-agent",
      createdAt: Date.now(),
    };
    if (userId) aiMeta.layer = userId;
    else aiMeta.isPersonal = true;

    // Ownership gate: the AI agent operates on the student's personal canvas only.
    // Shared/teacher records on the same editor.store must be untouchable —
    // a clear/delete on a teacher shape would leak past the routing patch and
    // erase content the student doesn't own. Mirrors the predicate used by the
    // routing patch (spaces-diff-filter) and StudentWorkCanvas guards
    // (useStudentSpaceCanvases.ts:185, teacherSpaceCanvasComponents.tsx:194).
    const isPersonalShape = (
      s: { meta?: Record<string, unknown> } | undefined,
    ) =>
      !!s &&
      ((!!userId && s.meta?.layer === userId) || s.meta?.isPersonal === true);

    const pendingBindings: TLBindingCreate[] = [];
    const touchedShapeIds: TLShapeId[] = [];
    const removedShapeIds: TLShapeId[] = [];

    editor.store.mergeRemoteChanges(() => {
      switch (action._type) {
        case "create": {
          const shape = action.shape as Record<string, unknown>;
          if (!shape || typeof shape._type !== "string") return;

          // highlight and draw require encoded stroke points that the AI cannot generate
          if (shape._type === "highlight" || shape._type === "draw") return;

          // Ensure the shape ID is unique on this canvas
          let shapeId =
            typeof shape.shapeId === "string" ? shape.shapeId : "shape";
          while (editor.getShape(`shape:${shapeId}` as TLShapeId)) {
            shapeId = /(\d+)$/.test(shapeId)
              ? shapeId.replace(/(\d+)$/, (n) => String(Number(n) + 1))
              : `${shapeId}-1`;
          }

          const result = convertPartialFocusedShapeToTldrawShape(
            editor,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            { ...shape, shapeId } as any,
            { defaultShape: {}, complete: true },
          );
          if (!result.shape) return;
          editor.createShape({
            ...result.shape,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            meta: aiMeta as any,
          });
          touchedShapeIds.push(result.shape.id as TLShapeId);
          if (result.bindings) {
            pendingBindings.push(...result.bindings);
          }
          break;
        }

        case "label": {
          const shapeId = `shape:${action.shapeId}` as TLShapeId;
          const existing = editor.getShape(shapeId);
          // Refuse to edit teacher/shared shapes — AI is scoped to the student's personal canvas.
          if (!existing || !isPersonalShape(existing)) return;
          editor.updateShape({
            id: shapeId,
            type: existing.type,
            props: { richText: toRichText(String(action.text ?? "")) },
            meta: {
              ...(existing.meta as Record<string, unknown>),
              editedBy: "spaces-agent",
              editedAt: Date.now(),
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any);
          touchedShapeIds.push(shapeId);
          break;
        }

        case "delete": {
          const shapeId = `shape:${action.shapeId}` as TLShapeId;
          const existing = editor.getShape(shapeId);
          // Refuse to delete teacher/shared shapes.
          if (!existing || !isPersonalShape(existing)) return;
          editor.deleteShape(shapeId);
          removedShapeIds.push(shapeId);
          break;
        }

        case "clear": {
          // AI's "clear canvas" must scope to the student's own shapes only —
          // editor.getCurrentPageShapeIds() returns BOTH personal and shared
          // (teacher) records when collabMode=shared.
          const personalIds = [...editor.getCurrentPageShapeIds()].filter(
            (id) => isPersonalShape(editor.getShape(id)),
          );
          if (personalIds.length === 0) break;
          editor.deleteShapes(personalIds);
          removedShapeIds.push(...personalIds);
          break;
        }

        case "move": {
          const shapeId = `shape:${action.shapeId}` as TLShapeId;
          const existing = editor.getShape(shapeId);
          // Refuse to move teacher/shared shapes.
          if (!existing || !isPersonalShape(existing)) return;
          editor.updateShape({
            id: shapeId,
            type: existing.type,
            x: typeof action.x === "number" ? action.x : existing.x,
            y: typeof action.y === "number" ? action.y : existing.y,
            meta: {
              ...(existing.meta as Record<string, unknown>),
              editedBy: "spaces-agent",
              editedAt: Date.now(),
            },
          });
          touchedShapeIds.push(shapeId);
          break;
        }
      }
    });

    // Bindings must be created OUTSIDE mergeRemoteChanges: tldraw wraps shape
    // mutations inside ArrowBindingUtil.onAfterCreate in its own atomic op,
    // and nesting mergeRemoteChanges inside that throws.
    const createdBindingIds: string[] = [];
    for (const binding of pendingBindings) {
      editor.createBinding(binding);
      // After creation tldraw assigns a real id; resolve it from the editor.
      // pendingBindings.id is set by convertPartialFocusedShapeToTldrawShape.
      const id = (binding as { id?: string }).id;
      if (id) createdBindingIds.push(id);
    }

    // Manually mirror everything into personalStore: mergeRemoteChanges
    // bypassed the routing patch, so without this the AI shapes would live
    // only in the local editor.store and never reach the DO personal room.
    //
    // Bindings get the same treatment — their endpoint shapes are personal
    // (we just created them), so the personal store must own the binding too,
    // otherwise: shape lands in personal room, binding leaks into the shared
    // room (createBinding ran inside tldraw's atomic op so our routing patch
    // fell back to a plain origPut), and on reconnect the binding can't find
    // its endpoint shapes in shared and gets dropped — leaving a free-floating
    // arrow with no anchor.
    if (personalStore) {
      if (touchedShapeIds.length > 0) {
        const records = touchedShapeIds
          .map((id) => editor.getShape(id))
          .filter((s): s is NonNullable<typeof s> => !!s);
        if (records.length > 0) personalStore.put(records);
      }
      if (createdBindingIds.length > 0) {
        const bindingRecords = createdBindingIds
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .map((id) => editor.store.get(id as any))
          .filter((b): b is NonNullable<typeof b> => !!b);
        // Mirror into the personal store so the DO personal room and any
        // teacher in Student Work see the binding alongside its personal
        // endpoint shapes.
        //
        // KNOWN LIMITATION: editor.createBinding ran outside mergeRemoteChanges
        // (tldraw wraps it in its own user-source atomic op) so the shared
        // useSync has ALREADY broadcast a `put binding` to the shared room by
        // this point. We can't reach across to undo that — it lives on as a
        // dangling binding in shared until the room is GC'd. Endpoint shapes
        // are personal so other clients in the shared room won't be able to
        // resolve fromId/toId and will drop the binding on next reconnect,
        // which keeps the visual outcome correct even though the metadata
        // briefly leaks.
        if (bindingRecords.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          personalStore.put(bindingRecords as any);
        }
      }
      if (removedShapeIds.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        personalStore.remove(removedShapeIds as any);
      }
    }
  } catch (err) {
    console.warn(
      "[spaces] canvas action failed:",
      err,
      "\naction:",
      JSON.stringify(action, null, 2),
    );
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

type PcmPlayerInstance = {
  feed: (data: ArrayBufferLike) => void;
  flush: () => void;
  clear: () => void;
  audioCtx?: AudioContext;
  startTime?: number;
};

/**
 * Manages the canvas WS connection for the spaces AI chat (text + optional voice).
 * Connects to /canvas/ws?spaceId={spaceId}&token={token}[&voiceEnabled=true&...].
 * Uses the same pako-compressed chunk protocol as the regular canvas agent.
 *
 * Reconnects automatically with exponential backoff (1s → 2s → 4s … capped at 30s)
 * except when the server explicitly closes with code 4001 (auth failure).
 */
export function useSpaceAgent(
  spaceId: string,
  lessonContext: string | null,
  getCanvasContext?: () => SpaceCanvasContext,
  getScreenshot?: () => Promise<{
    type: "screenshot";
    screenshot: string;
  } | null>,
  getEditor?: () => Editor | null,
  voiceModel?: string | null,
  voiceId?: string | null,
  voiceProvider?: string | null,
  language?: string | null,
  enabled?: boolean,
  // Student userId — stamped onto every AI-emitted shape's meta.layer so the
  // routing patch in spaces-diff-filter treats AI writes as personal-layer
  // edits, identical to shapes the student draws by hand.
  studentUserId?: number | null,
  // Personal sync store handle. AI mutations bypass the routing patch (they
  // run inside mergeRemoteChanges) so we mirror touched records into it
  // manually — without that, the AI shapes never reach the DO personal room.
  getPersonalStore?: () => TLStore | null,
) {
  const token = useAuthStore((s) => s.token);
  const gatewayBase = import.meta.env.VITE_GATEWAY_BASE_URL as string;
  const { emit, pcmPlayerRef: runtimePcmPlayerRef } = useRuntimeContext();

  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    try {
      return JSON.parse(
        sessionStorage.getItem(storageKey(spaceId)) ?? "[]",
      ) as ChatMessage[];
    } catch {
      return [];
    }
  });
  const [agentPhase, setAgentPhase] = useState<AgentPhase>("idle");
  const [isWsOpen, setIsWsOpen] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const activeMsgIdRef = useRef("");
  const activeTurnIdRef = useRef("");
  const pcmPlayerRef = useRef<PcmPlayerInstance | null>(null);
  const firstContentReceivedRef = useRef(false);
  // Created lazily on first sendAudio call; reset to null after finalizeAudio.
  const voiceTurnIdRef = useRef<string | null>(null);
  // If the server never sends CANVAS_START/DONE, we must not leave the loading
  // spinner visible forever — clear it after this timeout.
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Create PCMPlayer once on mount so AudioContext is warm before first turn.
  // Publish to RuntimeContext.pcmPlayerRef so the shared useCaptionsQueue can
  // read pcmPlayer.startTime / audioCtx.currentTime to compute the audio anchor.
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pcmPlayerRef.current = new (PCMPlayer as any)({
      inputCodec: "Int16",
      channels: 1,
      sampleRate: 44100,
    }) as PcmPlayerInstance;
    if (runtimePcmPlayerRef) runtimePcmPlayerRef.current = pcmPlayerRef.current;
    return () => {
      pcmPlayerRef.current?.clear();
      pcmPlayerRef.current = null;
      if (runtimePcmPlayerRef) runtimePcmPlayerRef.current = null;
    };
  }, [runtimePcmPlayerRef]);

  // Persist chat history to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem(storageKey(spaceId), JSON.stringify(messages));
  }, [messages, spaceId]);

  const clearLoadingTimeout = useCallback(() => {
    if (loadingTimeoutRef.current !== null) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, []);

  const startLoadingTimeout = useCallback(
    (turnId: string) => {
      clearLoadingTimeout();
      loadingTimeoutRef.current = setTimeout(() => {
        // Only cancel if this turn is still the active one
        if (activeTurnIdRef.current !== turnId) return;
        setAgentPhase("idle");
        activeMsgIdRef.current = "";
        // Remove the empty assistant bubble that was added on CANVAS_START (if any)
        setMessages((prev) =>
          prev.filter((m) => m.text.trim() !== "" || m.role === "user"),
        );
      }, LOADING_TIMEOUT_MS);
    },
    [clearLoadingTimeout],
  );

  // Connect WS with exponential backoff reconnect
  useEffect(() => {
    if (!spaceId || !token || enabled === false) return;

    const wsBase = gatewayBase.replace(/^http/, "ws");
    // Always include voice params when voiceModel is configured — this avoids
    // tearing down and reconnecting the WS when aiMode switches between
    // "voice" and "text-only" (those toggles don't change the room's voiceModel).
    const voiceParams = voiceModel
      ? `&voiceEnabled=true` +
        `&voiceModel=${encodeURIComponent(voiceModel)}` +
        (voiceId ? `&voiceId=${encodeURIComponent(voiceId)}` : "") +
        (voiceProvider
          ? `&voiceProvider=${encodeURIComponent(voiceProvider)}`
          : "") +
        (language ? `&language=${encodeURIComponent(language)}` : "")
      : "";
    const url = `${wsBase}/canvas/ws?spaceId=${encodeURIComponent(spaceId)}&token=${encodeURIComponent(token)}${voiceParams}`;
    let active = true;
    let retryCount = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    function connect() {
      if (!active) return;

      const ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      // Reset stale turn state so old chunk IDs don't suppress incoming messages
      const staleMsgId = activeMsgIdRef.current;
      activeMsgIdRef.current = "";
      activeTurnIdRef.current = "";
      setAgentPhase("idle");
      if (staleMsgId) {
        setMessages((prev) => prev.filter((m) => m.id !== staleMsgId));
      }

      ws.onopen = () => {
        if (!active) {
          ws.close();
          return;
        }
        retryCount = 0;
        setIsWsOpen(true);
      };

      ws.onmessage = (event: MessageEvent) => {
        let parsed: unknown;
        if (event.data instanceof ArrayBuffer) {
          parsed = inflateFrame(event.data);
        } else {
          try {
            parsed = JSON.parse(event.data as string);
          } catch (err) {
            console.warn("[useSpaceAgent] malformed text frame:", err);
            return;
          }
        }
        if (!parsed) return;

        // Handle server ping
        const asRecord = parsed as Record<string, unknown>;
        if (asRecord.type === "ping" && ws.readyState === WebSocket.OPEN) {
          sendCompressed(ws, { type: "pong" });
          return;
        }

        if (!isCanvasChunk(parsed)) return;
        const chunk = parsed;

        // CANVAS_USER_TRANSCRIPT — STT result for a voice turn; show as user bubble.
        // Arrives before CANVAS_START, so handle before the turn_id gate.
        if (chunk.chunk_type === CanvasChunkType.CANVAS_USER_TRANSCRIPT) {
          const transcript = chunk.chunk_data;
          if (typeof transcript === "string" && transcript.trim()) {
            setMessages((prev) => [
              ...prev,
              {
                id: randomId(),
                role: "user",
                text: transcript.trim(),
                timestamp: Date.now(),
              },
            ]);
          }
          return;
        }

        // Only process response chunks that belong to the active turn
        if (chunk.turn_id !== activeTurnIdRef.current) return;

        // CANVAS_AUDIO — PCM audio chunk from TTS; feed to player.
        if (chunk.chunk_type === CanvasChunkType.CANVAS_AUDIO) {
          if (!firstContentReceivedRef.current) {
            firstContentReceivedRef.current = true;
            clearLoadingTimeout();
            setAgentPhase("idle");
          }
          try {
            const { audio_chunk_b64 } = JSON.parse(chunk.chunk_data) as {
              audio_chunk_b64: string;
            };
            const player = pcmPlayerRef.current;
            if (!player) return;
            const raw = atob(audio_chunk_b64);
            const bytes = new Uint8Array(raw.length);
            for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
            player.feed(bytes.buffer);
          } catch {
            /* ignore */
          }
          return;
        }

        // CANVAS_CAPTIONS_UPDATE — per-token captions on the unified wire shape.
        // Forward to the shared tutor caption pipeline via RuntimeContext.
        if (chunk.chunk_type === CanvasChunkType.CANVAS_CAPTIONS_UPDATE) {
          emit("CAPTIONS_UPDATE", {
            chunk_data: chunk.chunk_data,
            turn_id: chunk.turn_id ?? "",
          });
          return;
        }

        if (chunk.chunk_type === CanvasChunkType.CANVAS_START) {
          emit("TURN_CHANGE", { source: "voice" });
          firstContentReceivedRef.current = false;
          activeMsgIdRef.current = randomId();
          setMessages((prev) => [
            ...prev,
            {
              id: activeMsgIdRef.current,
              role: "assistant",
              text: "",
              timestamp: Date.now(),
            },
          ]);
          return;
        }

        if (chunk.chunk_type === CanvasChunkType.CANVAS_ACTION) {
          try {
            const action = JSON.parse(chunk.chunk_data) as {
              _type?: string;
              text?: string;
              complete?: boolean;
              [key: string]: unknown;
            };
            if (action._type === "message" && typeof action.text === "string") {
              if (!firstContentReceivedRef.current) {
                firstContentReceivedRef.current = true;
                clearLoadingTimeout();
                setAgentPhase("idle");
              }
              const msgId = activeMsgIdRef.current;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === msgId ? { ...m, text: action.text! } : m,
                ),
              );
            } else if (
              action.complete &&
              action._type &&
              action._type !== "message"
            ) {
              // Canvas action — apply locally and mirror into the personal
              // sync store so the DO and any watching teacher see it.
              const editor = getEditor?.() ?? null;
              const pStore = getPersonalStore?.() ?? null;
              const uidStr =
                studentUserId !== null && studentUserId !== undefined
                  ? String(studentUserId)
                  : null;
              if (editor) {
                applySpaceCanvasAction(editor, action, pStore, uidStr);
              }
            }
          } catch (err) {
            console.warn("[useSpaceAgent] canvas action apply failed:", err);
          }
          return;
        }

        if (chunk.chunk_type === CanvasChunkType.CANVAS_DONE) {
          clearLoadingTimeout();
          setAgentPhase("idle");
          // Wait for the PCM queue to drain before freezing captions, so
          // the expand button appears only after audio actually finishes.
          const player = pcmPlayerRef.current;
          const endTime = player?.startTime;
          const audioCtx = player?.audioCtx;
          if (audioCtx && endTime !== undefined) {
            const poll = setInterval(() => {
              if (!pcmPlayerRef.current || audioCtx.currentTime >= endTime) {
                clearInterval(poll);
                emit("AUDIO_PLAYBACK_ENDED", undefined);
              }
            }, 100);
          } else {
            emit("AUDIO_PLAYBACK_ENDED", undefined);
          }
        }

        if (chunk.chunk_type === CanvasChunkType.CANVAS_ERROR) {
          clearLoadingTimeout();
          setAgentPhase("idle");
          emit("TURN_CHANGE", { source: "voice" });
        }
      };

      ws.onerror = () => {
        // onclose always fires after onerror — no extra action needed
      };

      ws.onclose = (event) => {
        if (!active) return; // superseded effect — new WS already in charge, don't clobber its state
        wsRef.current = null;
        setIsWsOpen(false);
        clearLoadingTimeout();
        setAgentPhase("idle");
        // Code 4001 = explicit auth rejection — do not retry
        if (event.code === 4001) return;
        const delay = Math.min(1_000 * Math.pow(2, retryCount), 30_000);
        retryCount++;
        retryTimer = setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      active = false;
      setIsWsOpen(false);
      if (retryTimer !== null) clearTimeout(retryTimer);
      wsRef.current?.close();
      wsRef.current = null;
      pcmPlayerRef.current?.clear();
    };
  }, [
    spaceId,
    token,
    gatewayBase,
    enabled,
    voiceModel,
    voiceId,
    voiceProvider,
    language,
    clearLoadingTimeout,
    emit,
  ]);

  const sendMessage = useCallback(
    async (text: string) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;

      const turnId = randomId();
      activeTurnIdRef.current = turnId;

      setMessages((prev) => [
        ...prev,
        {
          id: randomId(),
          role: "user",
          text,
          timestamp: Date.now(),
        },
      ]);

      // TURN_CHANGE — update gateway's currentTurnId before any chunks so that
      // isTurnCurrent() passes for this turn's actions (mirrors startVoiceTurn).
      sendCompressed(ws, {
        chunk_type: "TURN_CHANGE",
        turn_id: turnId,
        timestamp: Date.now(),
        chunk_data: JSON.stringify({ is_interrupt: false }),
      });

      // CANVAS_TEXT — send after TURN_CHANGE so the gateway processes the text for the registered turn.
      sendCompressed(
        ws,
        makeCanvasChunk(
          CanvasChunkType.CANVAS_TEXT,
          JSON.stringify({ user_message: text }),
          turnId,
        ),
      );

      // CANVAS_CONTEXT — mode + lessonContext + canvas state + conversation history.
      // blurryShapes and screenshot mirror the notepad agent's prompt parts so
      // buildMessages() puts them in the user message (not the system prompt).
      // History is captured before setMessages re-renders — excludes the current turn.
      // ORDER MATTERS: setAgentPhase before the async screenshot so the input is
      // disabled during the await and the user cannot submit a second message.
      firstContentReceivedRef.current = false;
      setAgentPhase("loading");
      startLoadingTimeout(turnId);

      const ctx = getCanvasContext?.() ?? null;
      const screenshotPart = (await getScreenshot?.()) ?? null;
      const conversationHistory = messages
        .filter((m) => m.text.trim())
        .slice(-20) // last 10 exchanges
        .map((m) => ({ role: m.role, text: m.text }));

      sendCompressed(
        ws,
        makeCanvasChunk(
          CanvasChunkType.CANVAS_CONTEXT,
          JSON.stringify({
            mode: { type: "mode", modeType: "notepad" },
            voiceOutput: false,
            ...(lessonContext ? { lessonContext } : {}),
            ...(ctx?.blurryShapes ? { blurryShapes: ctx.blurryShapes } : {}),
            ...(ctx?.userViewportBounds
              ? { userViewportBounds: ctx.userViewportBounds }
              : {}),
            ...(ctx?.selectedShapes
              ? { selectedShapes: ctx.selectedShapes }
              : {}),
            ...(ctx?.peripheralShapes
              ? { peripheralShapes: ctx.peripheralShapes }
              : {}),
            ...(screenshotPart ? { screenshot: screenshotPart } : {}),
            ...(conversationHistory.length ? { conversationHistory } : {}),
          }),
          turnId,
        ),
      );
    },
    [
      lessonContext,
      getCanvasContext,
      getScreenshot,
      messages,
      startLoadingTimeout,
    ],
  );

  /**
   * Call at mic-down, BEFORE startRecording.
   * If there is an active turn (AI is responding), sends `cancel` to stop
   * server-side generation and clears pending captions before starting the
   * new turn. Then sends TURN_CHANGE so the gateway closes any active STT
   * stream before the first audio chunk arrives.
   * The getUserMedia + AudioWorklet setup delay (~100-500ms) ensures
   * TURN_CHANGE is processed well before CANVAS_AUDIO chunks.
   */
  const startVoiceTurn = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    const prevTurnId = activeTurnIdRef.current;
    const isInterrupt = !!prevTurnId;

    if (isInterrupt) {
      sendCompressed(ws, { type: "cancel", turn_id: prevTurnId });
      pcmPlayerRef.current?.clear();
      emit("TURN_CHANGE", { source: "voice" });
    }
    setAgentPhase("listening");

    voiceTurnIdRef.current = randomId();
    activeTurnIdRef.current = voiceTurnIdRef.current;
    sendCompressed(ws, {
      chunk_type: "TURN_CHANGE",
      turn_id: voiceTurnIdRef.current,
      timestamp: Date.now(),
      chunk_data: JSON.stringify({ is_interrupt: isInterrupt }),
    });
  }, [emit]);

  /** Send a base64-encoded Int16 PCM audio chunk for the current voice turn. */
  const sendAudio = useCallback((b64: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn(
        "[useSpaceAgent] sendAudio: WS not open, state=",
        ws?.readyState ?? "null",
      );
      return;
    }
    if (!voiceTurnIdRef.current) {
      console.warn(
        "[useSpaceAgent] sendAudio: no voice turn ID — startVoiceTurn was not called",
      );
      return;
    }
    sendCompressed(
      ws,
      makeCanvasChunk(
        CanvasChunkType.CANVAS_AUDIO,
        JSON.stringify({ audio_chunk_b64: b64 }),
        voiceTurnIdRef.current,
      ),
    );
  }, []);

  /**
   * Signal end of audio stream. Also sends a CANVAS_CONTEXT with lesson
   * context + conversation history so the gateway has full context for the turn.
   */
  const finalizeAudio = useCallback(async () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !voiceTurnIdRef.current)
      return;
    const turnId = voiceTurnIdRef.current;

    const conversationHistory = messages
      .filter((m) => m.text.trim())
      .slice(-20)
      .map((m) => ({ role: m.role, text: m.text }));

    const ctx = getCanvasContext?.() ?? null;
    const screenshotPart = (await getScreenshot?.()) ?? null;

    // Re-check WS after async screenshot — it may have closed during await
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;

    sendCompressed(
      ws,
      makeCanvasChunk(
        CanvasChunkType.CANVAS_CONTEXT,
        JSON.stringify({
          mode: { type: "mode", modeType: "notepad" },
          voiceOutput: true,
          ...(lessonContext ? { lessonContext } : {}),
          ...(ctx?.blurryShapes ? { blurryShapes: ctx.blurryShapes } : {}),
          ...(ctx?.userViewportBounds
            ? { userViewportBounds: ctx.userViewportBounds }
            : {}),
          ...(ctx?.selectedShapes
            ? { selectedShapes: ctx.selectedShapes }
            : {}),
          ...(ctx?.peripheralShapes
            ? { peripheralShapes: ctx.peripheralShapes }
            : {}),
          ...(screenshotPart ? { screenshot: screenshotPart } : {}),
          ...(conversationHistory.length ? { conversationHistory } : {}),
        }),
        turnId,
      ),
    );

    firstContentReceivedRef.current = false;
    sendCompressed(
      ws,
      makeCanvasChunk(CanvasChunkType.CANVAS_AUDIO_DONE, "", turnId),
    );
    setAgentPhase("loading");
    startLoadingTimeout(turnId);
    // voiceTurnIdRef is intentionally NOT cleared here — the AudioWorklet buffer
    // may still drain trailing chunks after AUDIO_DONE. The server ignores them,
    // and the next startVoiceTurn will overwrite the ref with a new turnId anyway.
  }, [
    lessonContext,
    messages,
    getCanvasContext,
    getScreenshot,
    startLoadingTimeout,
  ]);

  return {
    messages,
    agentPhase,
    sendMessage,
    startVoiceTurn,
    sendAudio,
    finalizeAudio,
    isWsOpen,
  };
}
